export class Rect {
  x: number;
  y: number;
  width: number;
  height: number;

  constructor(x = 0, y = 0, width = 0, height = 0) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }

  public static fromDOMRect(rect: DOMRect) {
    return new Rect(rect.x, rect.y, rect.width, rect.height);
  }
  public static fromTouchEvent(event: TouchEvent) {
    return Array.from(event.touches).map(touch => new Rect(touch.clientX, touch.clientY));
  }
  public static fromTouch(touch: Touch) {
    return new Rect(touch.clientX, touch.clientY);
  }
  public static fromMouseEvent(event: MouseEvent) {
    return new Rect(event.clientX, event.clientY);
  }
  public static fromMouseEventScreen(event: MouseEvent) {
    return new Rect(event.screenX, event.screenY);
  }

  public add(rect: Rect) {
    return new Rect(this.x + rect.x, this.y + rect.y, this.width + rect.width, this.height + rect.height);
  }

  public subtract(rect: Rect) {
    return new Rect(this.x - rect.x, this.y - rect.y, this.width - rect.width, this.height - rect.height);
  }

  public times(num: number) {
    return new Rect(this.x * num, this.y * num, this.width * num, this.height * num);
  }

  public toRadians() {
    return Math.atan2(this.y, this.x); //radians
  }

  public toAngle() {
    const angle = Math.atan2(this.y, this.x); //radians
    // you need to devide by PI, and MULTIPLY by 180:
    const degrees = (180 * angle) / Math.PI; //degrees
    return (360 + Math.round(degrees)) % 360; //round number, avoid decimal fragments
  }

  public copy() {
    return new Rect(this.x, this.y, this.width, this.height);
  }

  public magnitude() {
    const x = this.x;
    const y = this.y;
    return Math.sqrt(x * x + y * y);
  }

  public contains(rect: Rect) {
    return (
      this.x <= rect.x &&
      this.y <= rect.y &&
      this.x + this.width >= rect.x + rect.width &&
      this.y + this.height >= rect.y + rect.height
    );
  }

  public rotatedPoints(angle: number) {
    const topLeft = new Rect(this.x, this.y);
    const topRight = new Rect(this.x + this.width, this.y);
    const bottomLeft = new Rect(this.x, this.y + this.height);
    const bottomRight = new Rect(this.x + this.width, this.y + this.height);
    const center = new Rect(this.x + this.width / 2, this.y + this.height / 2);
    const rotatedTopLeft = topLeft.rotateAroundPoint(center, angle);
    const rotatedTopRight = topRight.rotateAroundPoint(center, angle);
    const rotatedBottomLeft = bottomLeft.rotateAroundPoint(center, angle);
    const rotatedBottomRight = bottomRight.rotateAroundPoint(center, angle);

    return [rotatedTopLeft, rotatedTopRight, rotatedBottomRight, rotatedBottomLeft];
  }

  public containsWhenRotated(pos: Rect, angle: number) {
    const points = this.rotatedPoints(angle);
    return Rect.isInbetweenPoints(points, pos);
  }

  public rotateAroundPoint(point: Rect, angle: number) {
    // angle is in radians
    const x = this.x - point.x;
    const y = this.y - point.y;
    const x2 = x * Math.cos(angle) - y * Math.sin(angle);
    const y2 = x * Math.sin(angle) + y * Math.cos(angle);
    return new Rect(x2 + point.x, y2 + point.y, this.width, this.height);
  }

  public static isInbetweenPoints(points: Rect[], point: Rect) {
    const x = point.x;
    const y = point.y;
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x;
      const yi = points[i].y;
      const xj = points[j].x;
      const yj = points[j].y;
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) {
        inside = !inside;
      }
    }
    return inside;
  }

  public union(rect: Rect) {
    const x = Math.min(this.x, rect.x);
    const y = Math.min(this.y, rect.y);
    const width = Math.max(this.x + this.width, rect.x + rect.width) - x;
    const height = Math.max(this.y + this.height, rect.y + rect.height) - y;
    return new Rect(x, y, width, height);
  }

  public intersects(rect: Rect) {
    return (
      this.x < rect.x + rect.width &&
      this.x + this.width > rect.x &&
      this.y < rect.y + rect.height &&
      this.y + this.height > rect.y
    );
  }

  public equals(rect: Rect) {
    return this.x === rect.x && this.y === rect.y && this.width === rect.width && this.height === rect.height;
  }
}
